import { ReactNode } from 'react';
import { ChevronIcon } from '@ardoq/icons';
import { Header4, Text } from '@ardoq/typography';
import styled from 'styled-components';
import { FlexBox } from '@ardoq/layout';
import { colors } from '@ardoq/design-tokens';
import { WithPopover } from '@ardoq/popovers';
import { UnstyledButton } from '@ardoq/button';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

const Header = styled(FlexBox)`
  background-color: ${colors.grey50};
  border: solid 1px ${colors.grey300};
  border-radius: 4px 4px 0px 0px;
`;

const ToggleButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

type SurveyReferenceAndDependentQuestionHeaderProps = {
  isCollapsed: boolean;
  leftContent: ReactNode;
  requiredAnswersMissing: boolean;
  rightContent?: ReactNode;
  hasViewableSubQuestions?: boolean;
  i18nLocale?: I18nLocale;
  setIsCollapsed: (isCollapsed: boolean) => void;
};

export const SurveyReferenceAndDependentQuestionHeader = ({
  isCollapsed,
  leftContent,
  requiredAnswersMissing,
  rightContent,
  hasViewableSubQuestions = true,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  setIsCollapsed,
}: SurveyReferenceAndDependentQuestionHeaderProps) => {
  return (
    <>
      {isCollapsed && requiredAnswersMissing && (
        <Text color="textWarning" variant="caption">
          {t(
            i18nLocale,
            'surveysUi:priorityQuestionInDependent',
            'This question contains a priority question. Please provide a response.'
          )}
        </Text>
      )}
      <Header
        align="center"
        backgroundColor="bgSubtle"
        paddingX="large"
        paddingY="medium"
      >
        <ToggleButton
          data-test-id="collapse-button"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {hasViewableSubQuestions && (
            <ChevronIcon direction={isCollapsed ? 'down' : 'up'} />
          )}
          <WithPopover content={() => leftContent}>
            <Text truncate={{ lines: 1, onlyEllipsis: true }}>
              <Header4>{leftContent}</Header4>
            </Text>
          </WithPopover>
        </ToggleButton>
        {rightContent}
      </Header>
    </>
  );
};
